import { fmt } from './format';

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function money(value) {
  return fmt.currency(Number(value || 0));
}

export function buildReceiptHtml(receipt, settings = {}) {
  const storeName = settings.store_name || 'Your Store';
  const storeAddress = settings.store_address || '';
  const storePhone = settings.store_phone || '';
  const footer = settings.receipt_footer || 'Thank you for shopping with us.';
  const items = receipt?.items || [];

  const itemRows = items.map((item) => {
    const name = item.name || item.product_name || 'Item';
    const qty = Number(item.qty || 0);
    const total = Number(item.lineTotal || item.line_total || 0);
    return `
      <tr>
        <td>${escapeHtml(name)}</td>
        <td>${qty}</td>
        <td>${escapeHtml(money(total))}</td>
      </tr>
    `;
  }).join('');

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(storeName)} Receipt</title>
  <style>
    :root {
      color-scheme: light;
      --paper: #fffef8;
      --ink: #111827;
      --muted: #6b7280;
      --line: #e5e7eb;
      --accent: #111827;
    }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      background: linear-gradient(135deg, #eef2ff, #f8fafc 45%, #ecfeff);
      font-family: "Segoe UI", Arial, sans-serif;
      color: var(--ink);
      padding: 32px;
    }
    .shell {
      max-width: 860px;
      margin: 0 auto;
      display: grid;
      gap: 18px;
    }
    .hero {
      background: #0f172a;
      color: white;
      border-radius: 28px;
      padding: 24px 28px;
      box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
    }
    .hero h1 {
      margin: 0 0 8px;
      font-size: 28px;
      letter-spacing: -0.04em;
    }
    .hero p {
      margin: 0;
      color: rgba(255,255,255,0.72);
      font-size: 14px;
    }
    .receipt {
      background: var(--paper);
      border-radius: 28px;
      overflow: hidden;
      box-shadow: 0 28px 70px rgba(15, 23, 42, 0.18);
      border: 1px solid rgba(148, 163, 184, 0.18);
    }
    .receipt-head {
      padding: 28px 32px 18px;
      border-bottom: 1px solid var(--line);
      display: flex;
      justify-content: space-between;
      gap: 16px;
      align-items: flex-start;
    }
    .brand {
      max-width: 60%;
    }
    .brand h2 {
      margin: 0;
      font-size: 22px;
      letter-spacing: -0.03em;
    }
    .brand p, .meta p {
      margin: 6px 0 0;
      color: var(--muted);
      font-size: 13px;
      line-height: 1.5;
    }
    .meta {
      text-align: right;
    }
    .receipt-body {
      padding: 24px 32px 30px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
    }
    th, td {
      padding: 10px 0;
      border-bottom: 1px dashed var(--line);
      font-size: 14px;
      text-align: left;
    }
    th:last-child, td:last-child,
    th:nth-child(2), td:nth-child(2) {
      text-align: right;
    }
    .totals {
      margin-left: auto;
      width: min(320px, 100%);
      display: grid;
      gap: 10px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      font-size: 14px;
      color: var(--muted);
    }
    .grand {
      color: var(--accent);
      font-size: 20px;
      font-weight: 700;
      border-top: 2px solid var(--ink);
      padding-top: 12px;
      margin-top: 2px;
    }
    .footer {
      margin-top: 28px;
      background: #f8fafc;
      border-radius: 18px;
      padding: 14px 16px;
      color: var(--muted);
      font-size: 13px;
      line-height: 1.5;
    }
    .badge {
      display: inline-block;
      margin-top: 8px;
      padding: 6px 10px;
      border-radius: 999px;
      background: #dcfce7;
      color: #166534;
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    @media print {
      body { background: white; padding: 0; }
      .hero { display: none; }
      .receipt { box-shadow: none; border: none; border-radius: 0; }
    }
  </style>
</head>
<body>
  <div class="shell">
    <section class="hero">
      <h1>Receipt Preview</h1>
      <p>Use your browser print dialog to print or save this receipt as a PDF.</p>
    </section>
    <section class="receipt">
      <div class="receipt-head">
        <div class="brand">
          <h2>${escapeHtml(storeName)}</h2>
          ${storeAddress ? `<p>${escapeHtml(storeAddress)}</p>` : ''}
          ${storePhone ? `<p>${escapeHtml(storePhone)}</p>` : ''}
        </div>
        <div class="meta">
          <p><strong>Receipt</strong><br>${escapeHtml(receipt?.receipt_no || 'Pending')}</p>
          <p><strong>Date</strong><br>${escapeHtml(fmt.datetime(receipt?.created_at || new Date()))}</p>
          <span class="badge">${escapeHtml(receipt?.payment_method || 'paid')}</span>
        </div>
      </div>
      <div class="receipt-body">
        <table>
          <thead>
            <tr>
              <th>Item</th>
              <th>Qty</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            ${itemRows}
          </tbody>
        </table>
        <div class="totals">
          <div class="total-row"><span>Subtotal</span><span>${escapeHtml(money(receipt?.subtotal))}</span></div>
          <div class="total-row"><span>VAT</span><span>${escapeHtml(money(receipt?.tax_amt || receipt?.taxAmt))}</span></div>
          ${Number(receipt?.discount_pct || 0) > 0 ? `<div class="total-row"><span>Discount (${escapeHtml(receipt.discount_pct)}%)</span><span>- ${escapeHtml(money(receipt?.discount_amt || receipt?.discountAmt))}</span></div>` : ''}
          <div class="total-row grand"><span>Total</span><span>${escapeHtml(money(receipt?.total))}</span></div>
        </div>
        <div class="footer">${escapeHtml(footer)}</div>
      </div>
    </section>
  </div>
</body>
</html>`;
}

export function openReceiptPreview(receipt, settings = {}, { autoPrint = false } = {}) {
  const popup = window.open('', '_blank', 'width=960,height=900');
  if (!popup) return false;

  popup.document.open();
  popup.document.write(buildReceiptHtml(receipt, settings));
  popup.document.close();
  popup.focus();

  if (autoPrint) {
    popup.addEventListener('load', () => {
      popup.print();
    });
  }

  return true;
}
